/**
 * Presents business-unit membership by person so duplicate group assignments do
 * not obscure identity type or prevent user-level pagination.
 */
import React, { useEffect, useRef, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import { useTranslation } from 'src/arb-wrapper';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import type { BusinessUnitMemberDto } from 'src/store/generated/leela-api-generated';
import { useBusinessUnitAdminControllerFindBusinessUnitMembersQuery } from 'src/store/leela-api';
import { AddUserModal } from './AddUserModal';
import { EditUserModal } from './EditUserModal';
import { MembersTable } from './MembersTable';

interface MembersTabProps {
  businessUnitId: string;
}

const DEFAULT_PAGE_SIZE = 10;

export const MembersTab: React.FC<MembersTabProps> = ({ businessUnitId }) => {
  const { t } = useTranslation();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string>();
  const [page, setPage] = useState(1);
  const [prevBusinessUnitId, setPrevBusinessUnitId] = useState(businessUnitId);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const lastPagination = useRef<{
    businessUnitId: string;
    pageCount: number;
  }>();

  // This must precede the query hook to avoid fetching the new business unit with a stale page.
  if (prevBusinessUnitId !== businessUnitId) {
    setPrevBusinessUnitId(businessUnitId);
    setPage(1);
  }

  const { data, isError, isFetching, isLoading } =
    useBusinessUnitAdminControllerFindBusinessUnitMembersQuery({
      id: businessUnitId,
      page,
      pageSize,
    });

  const members = (data?.data ?? []) as BusinessUnitMemberDto[];
  const editingMember = members.find(
    (member) => member.userId === editingUserId,
  );
  const previousPageCount =
    lastPagination.current?.businessUnitId === businessUnitId
      ? lastPagination.current.pageCount
      : undefined;
  const pageCount = data?.meta.pagination.pageCount ?? previousPageCount ?? 0;
  const isInitialLoading = isLoading && previousPageCount === undefined;
  const isInitialError = isError && previousPageCount === undefined;
  const isPageTransitionLoading =
    isFetching && data === undefined && previousPageCount !== undefined;
  const isPageTransitionError = isError && previousPageCount !== undefined;

  useEffect(() => {
    if (data) {
      // Preserve pagination metadata so its control outlives uncached page transitions.
      lastPagination.current = {
        businessUnitId,
        pageCount: data.meta.pagination.pageCount,
      };
    }
  }, [businessUnitId, data]);

  useEffect(() => {
    // Wait for real pagination metadata so an in-flight page remains selected.
    if (data && pageCount > 0 && page > pageCount) setPage(pageCount);
  }, [data, page, pageCount]);

  if (isInitialLoading) return <Loader />;
  if (isInitialError) { return <Alert type="error">{t('access_management_load_error')}</Alert>; }

  return (
    <Column data-testid="access-members-tab" spacing="300">
      <Row alignmentHorizontal="end">
        <Button
          data-testid="access-members-add-button"
          type="primary"
          onClick={() => {
            setIsAddOpen(true);
          }}
        >
          <Icon tokens={plusTokens} />
          {t('access_management_add_users')}
        </Button>
      </Row>

      <MembersTable
        isPageTransitionError={isPageTransitionError}
        isPageTransitionLoading={isPageTransitionLoading}
        members={members}
        onEdit={(member) => {
          setEditingUserId(member.userId);
        }}
      />

      <TablePagination
        currentPage={page}
        itemsPerPage={pageSize}
        pageCount={Math.max(1, pageCount)}
        onItemsPerPageChange={(nextPageSize) => {
          setPage(1);
          setPageSize(nextPageSize);
        }}
        onPageChange={setPage}
      />

      <AddUserModal
        businessUnitId={businessUnitId}
        open={isAddOpen}
        onClose={() => {
          setIsAddOpen(false);
        }}
      />

      {editingMember && (
        <EditUserModal
          member={editingMember}
          open
          onClose={() => {
            setEditingUserId(undefined);
          }}
        />
      )}
    </Column>
  );
};
